<template>
  <a-drawer
    :visible="visible"
    :title="drawerTitle"
    :width="900"
    placement="right"
    :destroy-on-close="true"
    @close="handleClose"
  >
    <a-form
      :model="formData"
      layout="vertical"
      ref="formRef"
    >
      <!-- 회사 기본 정보 -->
      <a-divider orientation="left">회사 기본 정보</a-divider>

      <a-form-item
        label="회사 ID"
        name="companyId"
        :rules="[{ required: true, message: '회사 ID를 입력하세요' }]"
      >
        <a-input
          v-model:value="formData.companyId"
          placeholder="64914728bf6d973553eff0ae"
          :disabled="mode === 'edit'"
        />
      </a-form-item>

      <a-form-item
        label="회사명"
        name="companyName"
        :rules="[{ required: true, message: '회사명을 입력하세요' }]"
      >
        <a-input
          v-model:value="formData.companyName"
          placeholder="Certilife"
        />
      </a-form-item>

      <!-- 채널 목록 -->
      <a-divider orientation="left">
        채널 목록
        <a-button
          type="link"
          size="small"
          @click="addChannel"
          style="margin-left: 10px;"
        >
          <plus-outlined /> 채널 추가
        </a-button>
      </a-divider>

      <div v-if="formData.channels.length === 0" style="text-align: center; padding: 20px; color: #999;">
        최소 1개 이상의 채널이 필요합니다. "채널 추가" 버튼을 클릭하세요.
      </div>

      <div
        v-for="(channel, index) in formData.channels"
        :key="index"
        style="border: 1px solid #d9d9d9; border-radius: 4px; padding: 16px; margin-bottom: 16px; position: relative;"
      >
        <a-button
          type="link"
          danger
          size="small"
          style="position: absolute; top: 8px; right: 8px;"
          @click="removeChannel(index)"
        >
          <delete-outlined /> 삭제
        </a-button>

        <h4 style="margin-top: 0;">채널 {{ index + 1 }}</h4>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              :label="`채널 ID`"
              :name="['channels', index, 'channelId']"
              :rules="[{ required: true, message: '채널 ID를 입력하세요' }]"
            >
              <a-input
                v-model:value="channel.channelId"
                placeholder="@certilife"
              />
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item
              :label="`채널명`"
              :name="['channels', index, 'channelName']"
              :rules="[{ required: true, message: '채널명을 입력하세요' }]"
            >
              <a-input
                v-model:value="channel.channelName"
                placeholder="Certilife 알림톡"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              :label="`Sender Key`"
              :name="['channels', index, 'senderKey']"
              :rules="[{ required: true, message: 'Sender Key를 입력하세요' }]"
            >
              <a-input
                v-model:value="channel.senderKey"
                placeholder="sender-key-123"
              />
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item
              :label="`Client ID`"
              :name="['channels', index, 'clientId']"
              :rules="[{ required: true, message: 'Client ID를 입력하세요' }]"
            >
              <a-input
                v-model:value="channel.clientId"
                placeholder="R000000095"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-form-item
          :label="`Client Secret`"
          :name="['channels', index, 'clientSecret']"
          :rules="[{ required: true, message: 'Client Secret을 입력하세요' }]"
        >
          <a-input-password
            v-model:value="channel.clientSecret"
            placeholder="Client Secret을 입력하세요"
          />
        </a-form-item>
      </div>
    </a-form>

    <!-- 하단 버튼 -->
    <div class="drawer-footer">
      <a-space>
        <a-button @click="handleClose">취소</a-button>
        <a-button
          type="primary"
          class="yellow-button"
          @click="handleSave"
          :loading="saving"
        >
          저장
        </a-button>
      </a-space>
    </div>
  </a-drawer>
</template>

<script>
import { defineComponent, ref, watch, computed } from 'vue';
import { message } from 'ant-design-vue';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons-vue';

export default defineComponent({
  name: 'CompanyFormDrawer',
  components: {
    PlusOutlined,
    DeleteOutlined
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyData: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create', // 'create', 'edit'
      validator: (value) => ['create', 'edit'].includes(value)
    }
  },
  emits: ['update:visible', 'save', 'close'],
  setup(props, { emit }) {
    const formRef = ref(null);
    const saving = ref(false);

    const formData = ref({
      companyId: '',
      companyName: '',
      channels: []
    });

    const drawerTitle = computed(() =>
      props.mode === 'create' ? '인증정보 추가' : '인증정보 수정'
    );

    // 폼 초기화
    const resetForm = () => {
      formData.value = {
        companyId: '',
        companyName: '',
        channels: []
      };
    };

    // 채널 추가
    const addChannel = () => {
      formData.value.channels.push({
        channelId: '',
        channelName: '',
        senderKey: '',
        clientId: '',
        clientSecret: ''
      });
    };

    // 채널 삭제
    const removeChannel = (index) => {
      formData.value.channels.splice(index, 1);
    };

    // 회사 데이터 변경 감지
    watch(() => props.companyData, (newData) => {
      if (newData) {
        formData.value = {
          companyId: newData.companyId || '',
          companyName: newData.companyName || '',
          channels: newData.channels ? JSON.parse(JSON.stringify(newData.channels)) : []
        };
      } else {
        resetForm();
      }
    }, { immediate: true });

    // 닫기
    const handleClose = () => {
      emit('update:visible', false);
      emit('close');
      resetForm();
    };

    // 저장
    const handleSave = async () => {
      try {
        await formRef.value.validate();

        // 필수 필드 검증
        if (!formData.value.companyId || !formData.value.companyName) {
          message.warning('회사 ID와 회사명을 입력해주세요.');
          return;
        }

        if (formData.value.channels.length === 0) {
          message.warning('최소 1개 이상의 채널이 필요합니다.');
          return;
        }

        // 채널 ID 중복 검사
        const channelIds = formData.value.channels.map(c => c.channelId);
        const uniqueChannelIds = new Set(channelIds);
        if (channelIds.length !== uniqueChannelIds.size) {
          message.warning('채널 ID가 중복되었습니다. 각 채널 ID는 고유해야 합니다.');
          return;
        }

        saving.value = true;

        // 저장할 데이터
        const saveData = {
          companyId: formData.value.companyId,
          companyName: formData.value.companyName,
          channels: formData.value.channels.map(channel => ({
            channelId: channel.channelId,
            channelName: channel.channelName,
            senderKey: channel.senderKey,
            clientId: channel.clientId,
            clientSecret: channel.clientSecret
          }))
        };

        emit('save', saveData);

      } catch (error) {
        console.error('폼 검증 오류:', error);
      } finally {
        saving.value = false;
      }
    };

    return {
      formRef,
      saving,
      formData,
      drawerTitle,
      addChannel,
      removeChannel,
      handleClose,
      handleSave
    };
  }
});
</script>

<style scoped>
.drawer-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

.yellow-button {
  background-color: #FEE500;
  border-color: #FEE500;
  color: #000000;
}

.yellow-button:hover {
  background-color: #FFD700;
  border-color: #FFD700;
  color: #000000;
}

:deep(.ant-divider-horizontal.ant-divider-with-text-left::before) {
  width: 5%;
}

:deep(.ant-divider-horizontal.ant-divider-with-text-left::after) {
  width: 95%;
}
</style>
